import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { notificationTemplateApi } from '@/services/notificationApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, Edit, Trash2, Mail, Copy, Play, XCircle } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { NotificationTemplate, TemplateStatus } from '@/types/notification';

export const NotificationTemplateDetailPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testVariables, setTestVariables] = useState<Record<string, string>>({});
  const [testOutput, setTestOutput] = useState<string>('');
  const { hasPermission } = useAdminAuth();
  const { toast } = useToast();

  const canEdit = hasPermission('edit', 'notificationTemplate');
  const canDelete = hasPermission('delete', 'notificationTemplate');

  // Use entity hook for fetching template
  const {
    data: template,
    isLoading,
    error,
    refetch,
  } = useEntity<NotificationTemplate>({
    fetchFn: (id: string) => notificationTemplateApi.getById(id),
    entityId: templateId,
    entityName: 'notificationTemplate',
    enabled: !!templateId,
  });

  const isSystemTemplate = template?.metadata?.createdBy === 'system';

  useEffect(() => {
    if (searchParams.get('test') === 'true' && template) {
      setTestModalOpen(true);
    }
  }, [searchParams, template]);

  useEffect(() => {
    if (template && testModalOpen) {
      // Pre-fill test variables with defaultVariables
      const vars: Record<string, string> = {};
      if (template.defaultVariables) {
        Object.entries(template.defaultVariables).forEach(([key, value]) => {
          vars[key] = String(value);
        });
      }
      setTestVariables(vars);
    }
  }, [template, testModalOpen]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!templateId) throw new Error('Template ID is required');
      await notificationTemplateApi.delete(templateId);
    },
    entityName: 'notificationTemplate',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/notifications/templates';
    },
  });

  // Status change mutation
  const statusChangeMutation = useMutation({
    mutationFn: async (newStatus: TemplateStatus) => {
      if (!templateId) throw new Error('Template ID is required');
      await notificationTemplateApi.updateStatus(templateId, newStatus);
    },
    entityName: 'notificationTemplate',
    operation: 'update',
    onSuccess: () => {
      refetch();
    },
  });

  // Test mutation
  const testMutation = useMutation({
    mutationFn: async (variables: Record<string, string>) => {
      if (!templateId) throw new Error('Template ID is required');
      // API expects variables directly, not wrapped in a 'variables' key
      return await notificationTemplateApi.test(templateId, variables);
    },
    entityName: 'notificationTemplate',
    operation: 'custom',
  });

  const handleDelete = async () => {
    if (!templateId || !window.confirm('Are you sure you want to delete this template?')) return;

    const confirmId = window.prompt(`Type the template ID to confirm deletion: ${templateId}`);
    if (confirmId !== templateId) {
      toast({
        variant: 'destructive',
        title: 'Cancelled',
        description: 'Template ID did not match',
      });
      return;
    }
    await deleteMutation.mutate();
  };

  const handleStatusChange = async (newStatus: TemplateStatus) => {
    if (!templateId) return;
    if ((newStatus === 'ARCHIVED' || newStatus === 'INACTIVE') && !window.confirm(`Are you sure you want to ${newStatus.toLowerCase()} this template?`)) {
      return;
    }
    await statusChangeMutation.mutate(newStatus);
  };

  const handleTest = async () => {
    if (!templateId) return;
    const output = await testMutation.mutateAsync(testVariables);
    setTestOutput(output);
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      variant: 'success',
      title: 'Copied',
      description: 'Copied to clipboard',
    });
  };

  const extractVariables = (content: string | null | undefined): string[] => {
    if (!content) return [];
    const variables = new Set<string>();
    
    // Match Handlebars syntax: {{variableName}} or {{#if variableName}} or {{#each variableName}}
    // Also support Thymeleaf syntax: ${variableName} for backward compatibility
    const handlebarsRegex = /\{\{([#/]?)([^}]+)\}\}/g;
    const thymeleafRegex = /\$\{([^}]+)\}/g;
    
    // Handlebars helpers that should be skipped (not variables)
    const handlebarsHelpers = new Set(['if', 'each', 'unless', 'with', 'else', 'elseif']);
    
    // Extract Handlebars variables
    const handlebarsMatches = content.matchAll(handlebarsRegex);
    for (const match of handlebarsMatches) {
      const prefix = match[1]; // # or / or empty
      const fullMatch = match[2].trim(); // The content inside {{ }}
      
      // Skip closing tags like {{/if}}, {{/each}}
      if (prefix === '/') {
        continue;
      }
      
      // For helpers like {{#if variableName}}, extract the variable after the helper name
      if (prefix === '#') {
        const parts = fullMatch.split(/\s+/);
        if (parts.length > 0) {
          const helperName = parts[0].trim();
          // If it's a known helper, extract the variable after it
          if (handlebarsHelpers.has(helperName) && parts.length > 1) {
            const varName = parts[1].split('.')[0].split('(')[0].trim();
            if (varName && !handlebarsHelpers.has(varName)) {
              variables.add(varName);
            }
          } else if (!handlebarsHelpers.has(helperName)) {
            // If it's not a known helper, treat the whole thing as a variable
            const varName = fullMatch.split('.')[0].split('(')[0].trim();
            if (varName) {
              variables.add(varName);
            }
          }
        }
      } else {
        // Regular variable like {{name}} - no prefix
        const varName = fullMatch.split('.')[0].split('(')[0].trim();
        if (varName && !handlebarsHelpers.has(varName)) {
          variables.add(varName);
        }
      }
    }
    
    // Extract Thymeleaf variables for backward compatibility
    const thymeleafMatches = content.matchAll(thymeleafRegex);
    for (const match of thymeleafMatches) {
      const varName = match[1].split('.')[0].split('(')[0].trim();
      if (varName && !varName.startsWith('#')) {
        variables.add(varName);
      }
    }
    
    return Array.from(variables).sort();
  };

  if (isLoading) {
    return <LoadingState message="Loading notification template..." />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  if (!template) {
    return <ErrorState error={new Error('Notification template not found')} onRetry={refetch} />;
  }

  const detectedVariables = [
    ...extractVariables(template.content),
    ...(template.htmlContent ? extractVariables(template.htmlContent) : []),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/notifications/templates">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {template.name}
            </h2>
            <p className="text-muted-foreground mt-1">Template ID: {template.templateId}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setTestModalOpen(true)}>
            <Play className="mr-2 h-4 w-4" />
            Test
          </Button>
          {canEdit && (
            <Button asChild>
              <Link to={`/admin/notifications/templates/${template.templateId}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* System Template Warning */}
      {isSystemTemplate && (
        <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
              <XCircle className="h-5 w-5" />
              <p className="text-sm font-medium">
                This is a system-generated template. Editing may affect system functionality.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Quick Change */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Label>Status:</Label>
              <Select
                value={template.status}
                onValueChange={(value) => handleStatusChange(value as TemplateStatus)}
                disabled={!canEdit || statusChangeMutation.isLoading}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                  <SelectItem value="ARCHIVED">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to={`/admin/notifications/templates/new?clone=${template.templateId}`}>
                  <Copy className="mr-2 h-4 w-4" />
                  Clone
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="variables">Variables & Metadata</TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Template Information</CardTitle>
              <CardDescription>Basic template details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <CopyableId id={template.templateId} label="Template ID" />
                <div className="space-y-2">
                  <Label>Name</Label>
                  <p className="mt-1">{template.name}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={template.description || ''} disabled />
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <Label>Channel</Label>
                  <Input value={template.channel} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Locale</Label>
                  <Input value={template.locale} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Input value={template.type} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        template.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : template.status === 'DRAFT'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : template.status === 'INACTIVE'
                          ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}
                    >
                      {template.status}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Content Tab */}
        <TabsContent value="content" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Template Content</CardTitle>
              <CardDescription>Template content and detected variables</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {template.channel === 'EMAIL' && template.subject && (
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input value={template.subject} disabled />
                </div>
              )}

              <div className="space-y-2">
                <Label>Text Content</Label>
                <textarea
                  className="flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                  value={template.content}
                  disabled
                />
              </div>

              {(template.type === 'HTML' || template.type === 'MIXED') && template.htmlContent && (
                <div className="space-y-2">
                  <Label>HTML Content</Label>
                  <textarea
                    className="flex min-h-[300px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                    value={template.htmlContent}
                    disabled
                  />
                </div>
              )}

              {detectedVariables.length > 0 && (
                <div className="space-y-2">
                  <Label>Detected Variables</Label>
                  <div className="flex flex-wrap gap-2">
                    {detectedVariables.map((variable) => (
                      <span
                        key={variable}
                        className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm font-mono"
                      >
                        {`${variable}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Variables & Metadata Tab */}
        <TabsContent value="variables" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Default Variables</CardTitle>
              <CardDescription>Default variable values</CardDescription>
            </CardHeader>
            <CardContent>
              {template.defaultVariables && Object.keys(template.defaultVariables).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(template.defaultVariables).map(([key, value]) => (
                    <div key={key} className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Key</Label>
                        <Input value={key} disabled className="font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Value</Label>
                        <Input value={String(value)} disabled />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No default variables</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Metadata</CardTitle>
              <CardDescription>Additional metadata</CardDescription>
            </CardHeader>
            <CardContent>
              {template.metadata && Object.keys(template.metadata).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(template.metadata).map(([key, value]) => (
                    <div key={key} className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Key</Label>
                        <Input value={key} disabled className="font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Value</Label>
                        <Input value={value} disabled />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No metadata</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Audit Information</CardTitle>
              <CardDescription>Template creation and modification history</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Created: </span>
                  <span>
                    {template.createdAt
                      ? new Date(template.createdAt).toLocaleString()
                      : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Updated: </span>
                  <span>
                    {template.updatedAt
                      ? new Date(template.updatedAt).toLocaleString()
                      : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Version: </span>
                  <span>{template.version || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Created By: </span>
                  <span>{template.createdBy || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Updated By: </span>
                  <span>{template.updatedBy || 'N/A'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Test Modal */}
      <Dialog open={testModalOpen} onOpenChange={setTestModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Test Template</DialogTitle>
            <DialogDescription>
              Enter variable values to test the template rendering
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Variables</Label>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {detectedVariables.length > 0 ? (
                  detectedVariables.map((variable) => (
                    <div key={variable} className="grid gap-2 md:grid-cols-2">
                      <Input
                        value={variable}
                        disabled
                        className="font-mono"
                        placeholder="Variable name"
                      />
                      <Input
                        value={testVariables[variable] || ''}
                        onChange={(e) =>
                          setTestVariables({ ...testVariables, [variable]: e.target.value })
                        }
                        placeholder="Variable value"
                      />
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No variables detected</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleTest} disabled={testMutation.isLoading}>
                {testMutation.isLoading ? 'Testing...' : 'Test'}
              </Button>
              {testOutput && (
                <Button
                  variant="outline"
                  onClick={() => handleCopyToClipboard(testOutput)}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy Output
                </Button>
              )}
            </div>
            {testOutput && (
              <div className="space-y-2">
                <Label>Rendered Output</Label>
                <textarea
                  className="flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                  value={testOutput}
                  readOnly
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationTemplateDetailPage;








